import { SITE_ORIGIN } from "../../../config/site";
import L from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";

/** Минимальная цена публичных тарифов (₽/мес), для Offer / сниппетов. */
export const LANDING_PRICE_FROM = 790;

/** Open Graph — абсолютный URL. */
export const LANDING_OG_IMAGE = `${SITE_ORIGIN}/icons/icon-512.png`;
export const LANDING_OG_IMAGE_ALT = `${L.productName} — CRM для спортивных клубов`;

/** Заголовок вкладки и og:title (без суффикса — добавится в Seo). */
export const LANDING_PAGE_TITLE = L.productTagline;

export const LANDING_PAGE_DESCRIPTION =
  `${L.productName} — CRM для спортивных клубов и тренеров: журнал посещаемости, абонементы, ` +
  `кабинеты под брендом клуба и PWA на телефоне. ${TRIAL_DAYS} дней бесплатно, от ${LANDING_PRICE_FROM} ₽/мес.`;

export const LANDING_PAGE_KEYWORDS = [
  L.productName,
  "CRM для спортивного клуба",
  "CRM для тренера",
  "спортивная секция",
  "журнал посещаемости",
  "абонементы спортивный клуб",
  "расписание тренировок",
  "кабинет родителей секция",
  "PWA для клуба",
  "учёт клиентов секции",
];

function faqToSchema(faq = []) {
  if (!Array.isArray(faq) || !faq.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

function buildLandingPageJsonLd({
  path,
  title,
  description,
  faq,
  appName = L.productName,
}) {
  const pageUrl = `${SITE_ORIGIN}${path || ""}`;
  const shortDesc = String(description || "").slice(0, 300);
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: appName,
      alternateName: ["Baysport", "Bay Sport", L.productName],
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "CRM",
      operatingSystem: "Web, iOS, Android",
      url: pageUrl,
      image: LANDING_OG_IMAGE,
      description: shortDesc,
      inLanguage: "ru-RU",
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "RUB",
        lowPrice: String(LANDING_PRICE_FROM),
        offerCount: 3,
        description: `${TRIAL_DAYS} дней бесплатного пробного периода, далее от ${LANDING_PRICE_FROM} ₽/мес`,
        url: pageUrl,
        availability: "https://schema.org/InStock",
      },
      brand: {
        "@type": "Brand",
        name: L.productName,
      },
      publisher: {
        "@type": "Organization",
        name: L.productName,
        url: SITE_ORIGIN,
        logo: LANDING_OG_IMAGE,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description: shortDesc,
      url: pageUrl,
      inLanguage: "ru-RU",
      isPartOf: {
        "@type": "WebSite",
        name: L.productName,
        url: SITE_ORIGIN,
      },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: LANDING_OG_IMAGE,
      },
      about: {
        "@type": "SoftwareApplication",
        name: L.productName,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement:
        !path || path === "/"
          ? [
              {
                "@type": "ListItem",
                position: 1,
                name: title,
                item: pageUrl,
              },
            ]
          : [
              {
                "@type": "ListItem",
                position: 1,
                name: "Главная",
                item: SITE_ORIGIN,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: title,
                item: pageUrl,
              },
            ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: L.productName,
      url: SITE_ORIGIN,
      logo: LANDING_OG_IMAGE,
      description: L.productTagline,
      sameAs: [],
    },
  ];

  const faqSchema = faqToSchema(faq);
  if (faqSchema) schemas.push(faqSchema);
  return schemas;
}

export function buildLandingJsonLd() {
  return buildLandingPageJsonLd({
    path: "/",
    title: LANDING_PAGE_TITLE,
    description: LANDING_PAGE_DESCRIPTION,
  });
}

// --- Лендинг: приложение клуба ---
export const APP_LANDING_PATH = "/sozdat-prilozhenie-dlya-kluba";
export const APP_LANDING_PAGE_TITLE =
  "Приложение для спортивного клуба на телефоне клиентов";
export const APP_LANDING_PAGE_DESCRIPTION =
  `Создайте приложение клуба (PWA) для клиентов: кабинет под вашим брендом, журнал, абонементы и соцсеть секции. ` +
  `${TRIAL_DAYS} дней бесплатно, от ${LANDING_PRICE_FROM} ₽/мес.`;
export const APP_LANDING_PAGE_KEYWORDS = [
  "приложение для спортивного клуба",
  "создать приложение для фитнес клуба",
  "PWA для спортивного клуба",
  "мобильное приложение секции",
  "кабинет клиента спортивный клуб",
  "соцсеть для спортивной секции",
  "брендированное приложение клуба",
];
export function buildAppLandingJsonLd(faq) {
  return buildLandingPageJsonLd({
    path: APP_LANDING_PATH,
    title: APP_LANDING_PAGE_TITLE,
    description: APP_LANDING_PAGE_DESCRIPTION,
    faq,
  });
}

// --- Лендинг: CRM для спортивного клуба ---
export const CRM_LANDING_PATH = "/crm-dlya-sportivnogo-kluba";
export const CRM_LANDING_PAGE_TITLE =
  "CRM для спортивного клуба — журнал, абонементы, команда";
export const CRM_LANDING_PAGE_DESCRIPTION =
  `CRM для спортивного клуба вместо Excel и чатов: общий журнал, абонементы, роли тренеров и кабинет родителей под брендом клуба. ` +
  `Онлайн-оплата. ${TRIAL_DAYS} дней бесплатно, от ${LANDING_PRICE_FROM} ₽/мес.`;
export const CRM_LANDING_PAGE_KEYWORDS = [
  "crm для спортивного клуба",
  "crm для спортивной секции",
  "crm для фитнес клуба",
  "программа учёта спортивной секции",
  "журнал посещаемости клуба",
  "абонементы спортивный клуб",
  "кабинет родителей спортивная секция",
  "учёт должников секция",
  "онлайн оплата абонементов",
  "система для спортивного клуба",
];
export function buildCrmLandingJsonLd(faq) {
  return buildLandingPageJsonLd({
    path: CRM_LANDING_PATH,
    title: CRM_LANDING_PAGE_TITLE,
    description: CRM_LANDING_PAGE_DESCRIPTION,
    faq,
  });
}

// --- Лендинг: журнал посещаемости ---
export const JOURNAL_LANDING_PATH = "/zhurnal-poseshchaemosti";
export const JOURNAL_LANDING_PAGE_TITLE =
  "Электронный журнал посещаемости спортивной секции";
export const JOURNAL_LANDING_PAGE_DESCRIPTION =
  `Электронный журнал посещаемости секции с телефона: отметили приход — занятие списалось с абонемента, родители видят посещения в кабинете. ` +
  `${TRIAL_DAYS} дней бесплатно, от ${LANDING_PRICE_FROM} ₽/мес.`;
export const JOURNAL_LANDING_PAGE_KEYWORDS = [
  "журнал посещаемости секции",
  "электронный журнал спортивной секции",
  "журнал посещаемости тренировок",
  "перекличка в спортивной секции",
  "учет посещаемости спорт",
  "приложение журнал посещаемости",
  "списание абонемента по посещению",
  "журнал для тренера секции",
];
export function buildJournalLandingJsonLd(faq) {
  return buildLandingPageJsonLd({
    path: JOURNAL_LANDING_PATH,
    title: JOURNAL_LANDING_PAGE_TITLE,
    description: JOURNAL_LANDING_PAGE_DESCRIPTION,
    faq,
  });
}

// --- Лендинг: для тренера ---
export const TRAINER_LANDING_PATH = "/dlya-trenera";
export const TRAINER_LANDING_PAGE_TITLE =
  "CRM для тренера — журнал, абонементы, кабинет родителей";
export const TRAINER_LANDING_PAGE_DESCRIPTION =
  `CRM для тренера спортивной секции: журнал на телефоне, абонементы, расписание и кабинет родителей. Без Excel и лишней админки. ` +
  `От ${LANDING_PRICE_FROM} ₽/мес. ${TRIAL_DAYS} дней бесплатно.`;
export const TRAINER_LANDING_PAGE_KEYWORDS = [
  "crm для тренера",
  "программа для тренера",
  "приложение для тренера секции",
  "учет группы тренера",
  "как вести спортивную секцию",
  "журнал посещаемости для тренера",
  "абонементы для тренера",
  "кабинет родителей для тренера",
];
export function buildTrainerLandingJsonLd(faq) {
  return buildLandingPageJsonLd({
    path: TRAINER_LANDING_PATH,
    title: TRAINER_LANDING_PAGE_TITLE,
    description: TRAINER_LANDING_PAGE_DESCRIPTION,
    faq,
  });
}
